// Cross-subdomain session bridge for *.focuscasting.com.
//
// Supabase persists sessions to localStorage by default, which is per-origin.
// To make a user logged in on focuscasting.com also recognized on
// agency.focuscasting.com (and vice versa) without a second login, we mirror
// the active session into a cookie scoped to `.focuscasting.com` and hydrate
// from that cookie on boot when localStorage is empty.
//
// This bridge is a no-op on every other host (lovable previews, custom
// agency domains like focusagency.site, localhost). Those continue to behave
// exactly as before.

import { supabase } from "@/integrations/supabase/client";

const ROOT_DOMAIN = ".focuscasting.com";
const COOKIE_NAME = "fc_sb_sess";
const MAX_AGE = 60 * 60 * 24 * 30; // 30 days

function isSharedHost(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "focuscasting.com" || h.endsWith(ROOT_DOMAIN);
}

function writeCookie(value: string) {
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; Domain=${ROOT_DOMAIN}; Path=/; Max-Age=${MAX_AGE}; SameSite=Lax${secure}`;
}

function clearCookie() {
  document.cookie = `${COOKIE_NAME}=; Domain=${ROOT_DOMAIN}; Path=/; Max-Age=0; SameSite=Lax`;
}

function readCookie(): { access_token: string; refresh_token: string } | null {
  const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]+)`));
  if (!m) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(m[1]));
    if (parsed?.access_token && parsed?.refresh_token) return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

let installed = false;

export async function initCrossSubdomainSession() {
  if (installed || !isSharedHost()) return;
  installed = true;

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      const cookie = readCookie();
      if (cookie) {
        await supabase.auth.setSession({
          access_token: cookie.access_token,
          refresh_token: cookie.refresh_token,
        });
      }
    } else {
      // Make sure cookie is in sync at boot.
      writeCookie(JSON.stringify({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      }));
    }
  } catch (e) {
    console.warn("[cross-subdomain-session] hydrate failed", e);
  }

  supabase.auth.onAuthStateChange((event, sess) => {
    if (!isSharedHost()) return;
    if (event === "SIGNED_OUT") {
      clearCookie();
      return;
    }
    if (sess?.access_token && sess?.refresh_token) {
      writeCookie(JSON.stringify({
        access_token: sess.access_token,
        refresh_token: sess.refresh_token,
      }));
    }
  });
}
